import React, { useState, useEffect, useRef, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useToasts } from "react-toast-notifications";
import { useSelector, useDispatch } from "react-redux";
import { useTeamContext } from "store/contexts/teamContext";
import CourseService from "services/CourseService";
import Loader from "components/Loader";
import Button from "components/CustomButton";
import FilterMyCourses from "components/CourseBuilder/FilterMyCourses/FilterMyCourses";
import UserCourseLists from "components/CourseBuilder/UserCourseLists/UserCourseLists";
import * as courseActions from "reteamStore/actions";

const calculateCourseProgress = (course, teamId, userProgress) => {
  if (course && course.assigned_teams) {
    const teamProgress = course.assigned_teams.find(t => Number(t.id) === Number(teamId))?.progress;
    if (teamProgress) {
      return teamProgress.progress_percentage || 0;
    }
  }
  
  const courseProgress = userProgress[course.id] || { completedModules: [], totalModules: 0 };
  if (courseProgress.totalModules === 0) return 0;
  return Math.round((courseProgress.completedModules.length / courseProgress.totalModules) * 100);
};

const getCourseStatus = (course, teamId, userProgress) => {
  if (course && course.assigned_teams) {
    const teamProgress = course.assigned_teams.find(t => Number(t.id) === Number(teamId))?.progress;
    if (teamProgress) {
      if (teamProgress.completed || teamProgress.progress_percentage === 100) return "Completed";
      if (teamProgress.progress_percentage > 0) return "In Progress";
      return "Not Started";
    }
  }
  
  const progress = calculateCourseProgress(course, teamId, userProgress);
  if (progress === 0) return "Not Started";
  if (progress === 100) return "Completed";
  return "In Progress";
};

const UserCourses = () => {
  const { team } = useTeamContext();
  const dispatch = useDispatch();
  const { total, page, isLoading, courses } = useSelector(
    (state) => state.myCoursesReducer
  );
  const { addToast } = useToasts();
  const isInitialMount = useRef(true);
  
  const [order_by, setOrderBy] = useState("oldest");
  const [status_id, setStatusId] = useState(null);
  
  const [userProgress] = useState(() => {
    const saved = localStorage.getItem(`userCourseProgress_${team?.id}`);
    return saved ? JSON.parse(saved) : {};
  });

  const LoaderStyle = {
    display: "flex",
    justifyContent: "center",
  };

  const fetchMyCourses = useCallback((pageNum = 1, orderBy = "oldest", courseStatus = null) => {
    if (!team?.id) return;
    
    dispatch(courseActions.setMyCoursesLoader(true));
    
    if (pageNum === 1) {
      dispatch(courseActions.clearMyCourses());
    }
    
    CourseService.getMyCourses(pageNum, 10, team.id, orderBy, courseStatus)
      .then(response => {
        if (response && response.data) {
          dispatch(
            courseActions.getMyCourses(
              response.data.data,
              response.data.meta?.total || 0
            )
          );
        }
        dispatch(courseActions.setMyCoursesLoader(false));
      })
      .catch(error => {
        console.error("Error fetching courses:", error);
        addToast("Failed to load courses", { appearance: "error" });
        dispatch(courseActions.setMyCoursesLoader(false));
      });
  }, [team?.id, dispatch, addToast]);

  useEffect(() => {
    if (team?.id) {
      fetchMyCourses(1, order_by, status_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.id, fetchMyCourses]);

  const getNextPageData = () => {
    fetchMyCourses(page + 1, order_by, status_id);
    dispatch(courseActions.myCoursesPageIncrement());
  };

  const setTimelineCourses = () => {
    dispatch(courseActions.clearMyCourses());
  };

  const setPage = () => {
    // Reset page in redux to page 1
    dispatch(courseActions.clearMyCourses());
    // Set the internal page counter to 1
    for (let i = page; i > 1; i--) {
      dispatch(courseActions.coursePageIncreament());
    }
  };

  // Add useEffect to react to filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    fetchMyCourses(1, order_by, status_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_by, status_id]);

  // Calculate progress percentage for a course
  const calculateProgress = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return calculateCourseProgress(course, team?.id, userProgress);
  };

  // Get course status based on progress
  const getStatus = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return getCourseStatus(course, team?.id, userProgress);
  };

  const LoadingContainer = (
    <>
      {isLoading && (
        <h4>
          <Loader loaderStyle={LoaderStyle} />
        </h4>
      )}
      {!isLoading && (
        <h4>
          <Button onClick={getNextPageData}>Load More</Button>
        </h4>
      )}
    </>
  );

  if (isLoading && page === 1) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loader />
      </div>
    );
  }

  return (
    <div className="user-courses-container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3 tasks-btn">
        <h3 className="mb-0">My Courses</h3>
        <FilterMyCourses
          setStatusId={setStatusId}
          setPage={setPage}
          setIsLoading={(value) => dispatch(courseActions.setMyCoursesLoader(value))}
          setTimelineCourses={setTimelineCourses}
          setOrderBy={setOrderBy}
          order_by={order_by}
          status_id={status_id}
        />
      </div>

      <InfiniteScroll
        style={{ overflow: "hidden" }}
        dataLength={total ?? 0}
        next={getNextPageData}
        hasMore={courses.length !== total}
        loader={LoadingContainer}
        scrollableTarget="main-panel-right"
        endMessage={
          <div style={{ textAlign: "center" }}>
            {total > 0 && <div>Yay! You have seen all your courses</div>}
          </div>
        }
      >
        {courses.length === 0 ? (
          <div style={{ 
            display: "flex",
            width: "100%",
            justifyContent: "center",
            padding: "100px 0",
            fontSize: "22px",
            fontWeight: "500"
          }}>No courses assigned yet</div>
        ) : (
          <UserCourseLists 
            courses={courses} 
            total={total} 
            calculateProgress={calculateProgress}
            getStatus={getStatus}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

export default UserCourses; 